import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import CsvImportModal from "./CsvImportModal";
import { ConfidencePill, StatusPill } from "./Pills";
import { relativeTime } from "../lib/format";
import {
  X, FileText, Smartphone, Server, Cloud, RefreshCw, CheckCircle2,
  Zap, Droplets, Sun, Wifi, AlertTriangle, ExternalLink,
} from "lucide-react";

// Tab definitions ------------------------------------------------------------

const TABS = [
  { id: "manual",            label: "Manual / CSV", Icon: FileText },
  { id: "csv_import",        label: "CSV import",   Icon: FileText, hidden: true },
  { id: "mobile_local_sync", label: "Mobile sync",  Icon: Smartphone },
  { id: "bridge",            label: "Local bridge", Icon: Server },
  { id: "cloud_api",         label: "Cloud API",    Icon: Cloud },
];
// We display 4 visible tabs; "csv_import" is treated as the same tab as "manual".
const VISIBLE_TABS = TABS.filter((t) => !t.hidden);

const tabFromMode = (mode) => (mode === "csv_import" ? "manual" : mode);

// Per-source CSV import metadata --------------------------------------------
// Returns { endpoint, title, description, accent } for the Manual/CSV tab,
// or null if no CSV import flow exists for that source.
function csvImportFor(source, currentSite) {
  const lpkwh = currentSite?.liters_per_kwh ?? 500;
  const map = {
    "energy:homewizard": {
      endpoint: `/sites/${currentSite?.id}/imports/p1`,
      title: "Import HomeWizard P1 CSV",
      description: "Auto-detects daily vs cumulative readings; cumulative is converted to daily deltas.",
      accent: "#F59E0B",
      Icon: Zap,
    },
    "solar:fusionsolar": {
      endpoint: `/sites/${currentSite?.id}/imports/pv`,
      title: "Import FusionSolar PV CSV",
      description: "Daily PV yield. Existing grid import/export will not be overwritten.",
      accent: "#F59E0B",
      Icon: Sun,
    },
    "water:other": {
      endpoint: `/sites/${currentSite?.id}/imports/pump`,
      title: "Import pump cumulative kWh CSV",
      description: `Converted to liters using your site's liters_per_kwh (${lpkwh}).`,
      accent: "#3B82F6",
      Icon: Droplets,
    },
    "water:manual": {
      endpoint: `/sites/${currentSite?.id}/imports/water`,
      title: "Import manual water CSV",
      description: "Optional rain / consumption / tank columns. Existing values are preserved unless explicitly set.",
      accent: "#3B82F6",
      Icon: Droplets,
    },
  };
  return map[`${source.domain}:${source.provider}`] || null;
}

// Per-tab availability for a given source. Each tab gets a "state":
//   recommended     — the best mode available today for this source
//   active          — currently the source's connector_mode (overrides label)
//   available       — works but not the recommended choice
//   planned         — on the roadmap, polished placeholder visible
//   setup_required  — provider supports it but needs config (e.g. API key)
//   unavailable     — does not apply to this provider at all
function tabAvailability(source) {
  const a = {
    manual:            "available",
    mobile_local_sync: "planned",
    bridge:            "planned",
    cloud_api:         "unavailable",
  };

  if (source.provider === "open_meteo") {
    a.cloud_api = "recommended";
    a.manual = "unavailable";
  } else if (source.provider === "wunderground") {
    a.cloud_api = "setup_required";
    a.manual = "unavailable";
  } else if (source.provider === "homewizard") {
    // P1 cannot be reached from the cloud — CSV is the recommended path *now*.
    a.manual = "recommended";
    a.mobile_local_sync = "planned";
    a.bridge = "planned";
  } else if (source.provider === "fusionsolar") {
    a.manual = "recommended";
    a.cloud_api = "setup_required";
  } else if (source.provider === "manual") {
    a.manual = "recommended";
  } else if (source.provider === "other" && source.connector_mode === "bridge") {
    a.bridge = "setup_required";
    a.manual = "unavailable";
  } else if (source.provider === "other") {
    // Pump-socket / other CSV sources
    a.manual = "recommended";
  }
  return a;
}

// Pill metadata. "active" wins over the underlying state — but we render BOTH
// when the active tab is also the recommended mode (so users see "Active ·
// Recommended" without two pills).
const STATE_PILL = {
  active:         { text: "Active",          cls: "pill-connected" },
  recommended:    { text: "Recommended",     cls: "pill-stale" },     // amber
  available:      { text: "Available",       cls: "pill-manual" },
  planned:        { text: "Planned",         cls: "pill-simulated" }, // blue
  setup_required: { text: "Setup required",  cls: "pill-not_configured" },
  unavailable:    null,                                                 // hidden
};

// =============================================================================
// Tab bar
// =============================================================================

/**
 * Horizontal tab strip for the modal. Picking the right pill is the trickier
 * piece: the *active* mode (the source's saved connector_mode) wins over the
 * underlying recommendation, and when the active tab is also the recommended
 * choice we surface BOTH pills so users see "Active · Recommended".
 */
function ConnectorTabBar({ activeTab, currentMode, availability, onSelect }) {
  return (
    <div className="flex border-b border-border overflow-x-auto" role="tablist">
      {VISIBLE_TABS.map((t) => {
        const state = availability[t.id];
        const isActive = activeTab === t.id;
        const isCurrent = currentMode === t.id;
        const isUnavailable = state === "unavailable";
        const meta = isCurrent ? STATE_PILL.active : STATE_PILL[state] || null;
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={isActive}
            aria-disabled={isUnavailable}
            onClick={() => !isUnavailable && onSelect(t.id)}
            data-testid={`connector-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 py-3 border-b-2 transition-colors text-sm whitespace-nowrap ${
              isActive ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            } ${isUnavailable ? "opacity-40 cursor-not-allowed hover:text-muted-foreground" : ""}`}
          >
            <t.Icon className="w-3.5 h-3.5" />
            <span>{t.label}</span>
            {meta && (
              <span className={`pill ${meta.cls} ml-0.5`}>
                <span className="pill-dot" />{meta.text}
              </span>
            )}
            {isCurrent && state === "recommended" && (
              <span className={`pill ${STATE_PILL.recommended.cls} ml-1`}>
                <span className="pill-dot" />Recommended
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
// Footer
// =============================================================================

function ConnectorFooter({ activeTab, currentMode, busy, err, onCancel, onApply }) {
  const activeLabel = VISIBLE_TABS.find((t) => t.id === activeTab)?.label;
  const isUnchanged = currentMode === activeTab;
  return (
    <div className="border-t border-border px-5 py-3 flex items-center justify-between gap-3">
      <div className="text-xs text-muted-foreground">
        {isUnchanged ? (
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3 text-[#10B981]" /> This is the current mode for this source.
          </span>
        ) : (
          <span>Switching to <span className="text-foreground font-medium">{activeLabel}</span></span>
        )}
      </div>
      <div className="flex gap-2">
        {err && <span className="text-xs text-[#EF4444]">{err}</span>}
        <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm border border-border hover:bg-secondary" data-testid="connector-cancel">
          Cancel
        </button>
        <button
          onClick={onApply}
          disabled={busy || isUnchanged}
          className="px-3 py-1.5 rounded-md text-sm bg-foreground text-background font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="connector-apply"
        >
          {busy ? "Saving…" : isUnchanged ? "No changes" : "Save as default"}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Main modal
// =============================================================================

export default function ConnectorSetupModal({ open, onClose, source, currentSite, onUpdated }) {
  const [activeTab, setActiveTab] = useState(() =>
    source ? tabFromMode(source.connector_mode) || "manual" : "manual"
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [synced, setSynced] = useState(null);

  // Re-sync transient modal state whenever a *different* source is opened.
  // Track the last hydrated id in a ref so we can keep the dep array
  // exhaustive on `source` without wiping state on unrelated parent renders.
  const hydratedSourceIdRef = useRef(null);
  useEffect(() => {
    if (!source) {
      hydratedSourceIdRef.current = null;
      return;
    }
    if (hydratedSourceIdRef.current === source.id) return;
    hydratedSourceIdRef.current = source.id;
    setActiveTab(tabFromMode(source.connector_mode) || "manual");
    setErr(null);
    setSynced(null);
  }, [source]);

  if (!open || !source) return null;

  const availability = tabAvailability(source);
  const csvCfg = csvImportFor(source, currentSite);
  const currentMode = tabFromMode(source.connector_mode);

  const apply = async () => {
    if (activeTab === currentMode) { onClose(); return; }
    setBusy(true); setErr(null);
    try {
      const targetMode = activeTab === "manual" ? "csv_import" : activeTab;
      const updates = { connector_mode: targetMode };
      // For modes we can't fully wire yet, mark not_configured to be honest.
      if (["mobile_local_sync", "bridge"].includes(targetMode) && availability[activeTab] !== "active") {
        updates.status = "not_configured";
      }
      await api.patch(`/sites/${currentSite.id}/data-sources/${source.id}`, updates);
      onUpdated?.();
      onClose();
    } catch (e) {
      // 401s are handled globally (token cleared, AuthContext flips to
      // unauthenticated, router redirects to /login) — don't show a red box
      // mid-flow.
      if (e.isAuthFailure) {
        onClose();
      } else {
        setErr(e.userMessage || "Failed to update connector");
      }
    } finally {
      setBusy(false);
    }
  };

  const syncWeather = async () => {
    setBusy(true); setErr(null); setSynced(null);
    try {
      const { data } = await api.post(`/sites/${currentSite.id}/weather/sync?days=30`);
      setSynced({
        rows_created: data.rows_created || 0,
        rows_updated: data.rows_updated || 0,
        status: data.status,
      });
      onUpdated?.();
    } catch (e) {
      if (e.isAuthFailure) {
        onClose();
      } else {
        setErr(e.userMessage || "Open-Meteo sync failed");
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Trigger a Wunderground sync. Reads the saved api_key + station_id from
   * the source's config_json on the backend — we pass only the
   * data_source_id so the credentials never leave the server.
   */
  const syncWunderground = async () => {
    setBusy(true); setErr(null); setSynced(null);
    try {
      const { data } = await api.post(
        `/sites/${currentSite.id}/wunderground/sync?data_source_id=${source.id}&days=30`,
      );
      setSynced({
        rows_created: data.rows_created || 0,
        rows_updated: data.rows_updated || 0,
        status: data.status,
        errors: data.errors,
        warnings: data.warnings,
      });
      onUpdated?.();
    } catch (e) {
      if (e.isAuthFailure) {
        onClose();
      } else {
        setErr(e.userMessage || "Wunderground sync failed");
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Persist Wunderground credentials onto the data source's `config_json`.
   * The PATCH endpoint already accepts arbitrary `config_json` shapes, so
   * no schema change is required.
   */
  const saveWundergroundConfig = async (cfg) => {
    setBusy(true); setErr(null);
    try {
      await api.patch(`/sites/${currentSite.id}/data-sources/${source.id}`, {
        config_json: cfg,
        connector_mode: "cloud_api",
      });
      onUpdated?.();
    } catch (e) {
      setErr(e.userMessage || "Failed to save Wunderground configuration");
      throw e;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4 fade-up"
      data-testid="connector-modal"
    >
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-card border border-border rounded-2xl">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-border">
          <div className="min-w-0">
            <div className="label-tiny mb-1">Configure connector</div>
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              {source.notes ? source.notes.split("—")[0].trim() : source.provider}
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">{source.domain}</span>
            </h3>
            <div className="flex items-center gap-2 mt-2">
              <StatusPill status={source.status} />
              <ConfidencePill level={source.confidence_level} />
              <span className="text-[10px] text-muted-foreground">last sync {relativeTime(source.last_sync_at)}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary flex-shrink-0" data-testid="connector-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <ConnectorTabBar
          activeTab={activeTab}
          currentMode={currentMode}
          availability={availability}
          onSelect={setActiveTab}
        />

        {/* Tab content */}
        <div className="p-5 min-h-[280px]" data-testid={`connector-content-${activeTab}`}>
          {activeTab === "manual" && (
            <ManualPane source={source} csvCfg={csvCfg} onOpenImport={() => setCsvOpen(true)} />
          )}
          {activeTab === "mobile_local_sync" && <MobilePane />}
          {activeTab === "bridge" && <BridgePane />}
          {activeTab === "cloud_api" && (
            <CloudPane
              source={source}
              onSync={syncWeather}
              onWundergroundSync={syncWunderground}
              onWundergroundSave={saveWundergroundConfig}
              synced={synced}
              busy={busy}
            />
          )}
        </div>

        {/* Footer */}
        <ConnectorFooter
          activeTab={activeTab}
          currentMode={currentMode}
          busy={busy}
          err={err}
          onCancel={onClose}
          onApply={apply}
        />
      </div>

      {/* Re-use the existing CSV import modal so import logic stays identical. */}
      {csvCfg && (
        <CsvImportModal
          open={csvOpen}
          onClose={() => setCsvOpen(false)}
          onSuccess={() => onUpdated?.()}
          title={csvCfg.title}
          description={csvCfg.description}
          endpoint={csvCfg.endpoint}
        />
      )}
    </div>
  );
}

// =============================================================================
// Tab panes
// =============================================================================

function ManualPane({ source, csvCfg, onOpenImport }) {
  if (csvCfg) {
    const Icon = csvCfg.Icon;
    return (
      <div className="space-y-4">
        <Para>
          The universal connector — works for every provider, on every network,
          without an account anywhere else. Drop in a CSV from your device's
          companion app and Dreamhouse Flow handles the messy parts: delimiter
          detection, cumulative-vs-daily readings, date column inference and
          source-safe merging.
        </Para>
        <div className="card-base p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-md grid place-items-center flex-shrink-0" style={{ background: `${csvCfg.accent}20`, color: csvCfg.accent }}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">{csvCfg.title.replace("Import ", "")}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{csvCfg.description}</div>
          </div>
          <button
            onClick={onOpenImport}
            className="px-3 py-1.5 rounded-md bg-foreground text-background text-xs font-medium flex-shrink-0"
            data-testid="connector-open-import"
          >
            Open importer
          </button>
        </div>
        <Bullet>Source-safe merge — existing fields are never silently overwritten with null.</Bullet>
        <Bullet>Every import is logged as a SyncRun with rows created · updated · skipped.</Bullet>
        <Bullet>Re-imports are idempotent — drop the same file twice, get the same result.</Bullet>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <Para>
        This provider streams its data directly into Dreamhouse Flow, so a
        manual CSV path isn't necessary. You can still record values by hand
        from the relevant domain page if you ever need to reconcile.
      </Para>
      <DiscreetNote>Cloud APIs like Open-Meteo handle ingestion automatically — no exports required.</DiscreetNote>
    </div>
  );
}

function MobilePane() {
  return (
    <div className="space-y-4">
      <Para>
        <span className="text-foreground font-medium">Built around how local devices actually work.</span>{" "}
        HomeWizard P1, smart plugs and most local meters expose a clean local
        API but only respond inside your own network. The hosted web app
        deliberately doesn't try to reach <code className="font-mono text-xs bg-secondary px-1 py-0.5 rounded">192.168.x.x</code> from
        the cloud — that path is brittle and a security liability.
      </Para>
      <Para>
        <span className="text-foreground font-medium">The mobile app is the courier.</span>{" "}
        Open Dreamhouse on your phone while you're home. It discovers your
        devices, reads a snapshot from each local API, and pushes that snapshot
        to your cloud account over an authenticated channel. The cloud computes
        daily deltas and KPIs server-side. Your data stays yours; your network
        stays closed.
      </Para>
      <div className="grid sm:grid-cols-3 gap-3 mt-2">
        <Step n="1" Icon={Wifi}>Connect phone to home Wi-Fi</Step>
        <Step n="2" Icon={Smartphone}>Tap "Sync local devices"</Step>
        <Step n="3" Icon={Cloud}>Snapshot → cloud · KPIs derive</Step>
      </div>
      <DiscreetNote tone="warning">
        <AlertTriangle className="w-3 h-3 inline mr-1 text-[#F59E0B]" />
        Mobile sync ships in the next public release. The data path is already
        live in the API — until then, CSV import gives you the same data with
        a few extra clicks.
      </DiscreetNote>
    </div>
  );
}

function BridgePane() {
  return (
    <div className="space-y-4">
      <Para>
        <span className="text-foreground font-medium">For homes that want true automation.</span>{" "}
        A small always-on agent at home polls your local devices on a schedule
        and pushes readings to a token-protected ingest endpoint. No phone
        required. No CSVs. Survives reboots, Wi-Fi blips and your absence.
      </Para>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {["Mac mini", "Raspberry Pi", "Docker", "Home Assistant", "n8n"].map((p) => (
          <div key={p} className="text-center text-[11px] uppercase tracking-wider text-muted-foreground border border-border rounded-md py-2">
            {p}
          </div>
        ))}
      </div>
      <Para>
        Pick whatever you already run at home. The agent is a small process —
        a single binary, a Docker image or a Home Assistant add-on — that calls
        out to Dreamhouse Flow only. No inbound ports, no port forwarding, no
        DNS gymnastics.
      </Para>
      <div className="card-base p-4 font-mono text-[11px] text-muted-foreground space-y-1">
        <div className="text-foreground"># Bridge ingest contract</div>
        <div>POST /api/sites/&lt;site_id&gt;/ingest</div>
        <div>Authorization: Bearer &lt;bridge_token&gt;</div>
        <div>Content-Type: application/json</div>
        <div>{`{`} domain, provider, readings[ ] {`}`}</div>
      </div>
      <DiscreetNote tone="warning">
        <AlertTriangle className="w-3 h-3 inline mr-1 text-[#F59E0B]" />
        Bridge ships with the public beta. Per-site rotating tokens and a
        Docker image are scaffolded — the open-source agent follows in Phase 3.
      </DiscreetNote>
    </div>
  );
}

function CloudPane({ source, onSync, onWundergroundSync, onWundergroundSave, synced, busy }) {
  if (source.provider === "open_meteo") {
    return (
      <div className="space-y-4">
        <Para>
          <span className="text-foreground font-medium">Open-Meteo is live and recommended for weather.</span>{" "}
          Free, no API key, no signup. Daily aggregates for your site's exact
          coordinates are pulled on demand: rain, temp min/max, wind, gusts,
          dominant wind direction, UV index and shortwave solar radiation.
        </Para>
        <div className="card-base p-4 flex items-center justify-between">
          <div>
            <div className="font-medium text-sm flex items-center gap-2">
              <Cloud className="w-4 h-4 text-[#A1A1AA]" /> Open-Meteo · 30 day window
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">Last sync {relativeTime(source.last_sync_at)}</div>
          </div>
          <button
            onClick={onSync}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-foreground text-background text-xs font-medium disabled:opacity-50"
            data-testid="connector-cloud-sync"
          >
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {busy ? "Syncing…" : "Sync now"}
          </button>
        </div>
        {synced && (
          <div className="text-xs text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 rounded-md p-2.5 flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Synced · created {synced.rows_created}, updated {synced.rows_updated}.
          </div>
        )}
        <DiscreetNote>
          <ExternalLink className="w-3 h-3 inline mr-1" />
          Provider <a href="https://open-meteo.com/" target="_blank" rel="noreferrer" className="underline underline-offset-2">open-meteo.com</a> · CC-BY 4.0 · no key, no signup, generous rate limits.
        </DiscreetNote>
      </div>
    );
  }
  if (source.provider === "wunderground") {
    return (
      <WundergroundPane
        source={source}
        onSave={onWundergroundSave}
        onSync={onWundergroundSync}
        synced={synced}
        busy={busy}
      />
    );
  }
  return (
    <div className="space-y-3">
      <Para>This provider doesn't expose a public Cloud API today.</Para>
      <DiscreetNote>If your provider ships a REST API, open an issue and we'll evaluate adding it.</DiscreetNote>
    </div>
  );
}

// ---- Small UI atoms --------------------------------------------------------

function Para({ children }) {
  return <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>;
}
function Bullet({ children }) {
  return (
    <div className="flex items-start gap-2 text-sm text-muted-foreground">
      <span className="w-1 h-1 rounded-full bg-foreground mt-2 flex-shrink-0" /> {children}
    </div>
  );
}
function DiscreetNote({ children, tone = "default" }) {
  const cls = tone === "warning"
    ? "text-[#F59E0B] bg-[#F59E0B]/5 border-[#F59E0B]/20"
    : "text-muted-foreground border-border";
  return (
    <div className={`text-xs border rounded-md p-2.5 leading-relaxed ${cls}`}>
      {children}
    </div>
  );
}
function Step({ n, Icon, children }) {
  return (
    <div className="card-base p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-5 h-5 rounded-full border border-border grid place-items-center text-[10px] font-mono">{n}</span>
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      </div>
      <div className="text-xs">{children}</div>
    </div>
  );
}
function Field({ label, type = "text", placeholder, disabled, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="label-tiny block mb-1.5">{label}</span>
      <input
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        value={value ?? ""}
        onChange={onChange}
        data-testid={testid}
        className="w-full px-3 py-2 rounded-md bg-zinc-950 border border-border text-sm font-mono disabled:opacity-50 focus:border-zinc-500 outline-none"
      />
    </label>
  );
}

/**
 * Wunderground configuration + live sync. Configuration is persisted to the
 * data source's `config_json`; the actual API key never lives in the
 * frontend bundle and is only round-tripped from MongoDB during edits.
 *
 * UX states surfaced via the small status pill:
 *   - Not configured: api_key or station_id missing
 *   - Configured / Last sync: both fields set + last_sync_at present
 *   - Error: last sync flipped the source's status to "error"
 */
function WundergroundPane({ source, onSave, onSync, synced, busy }) {
  const cfg = source.config_json || {};
  const [apiKey, setApiKey] = useState(cfg.api_key || "");
  const [stationId, setStationId] = useState(cfg.station_id || "");
  const [savedCfg, setSavedCfg] = useState(cfg); // mirrors what's persisted; updates after Save
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  // Re-hydrate the local "saved" mirror whenever the source prop refreshes
  // (e.g. after the parent's onUpdated -> reload). Tracking the last seen id
  // keeps form state intact across unrelated re-renders.
  const lastSourceIdRef = useRef(source.id);
  useEffect(() => {
    if (lastSourceIdRef.current !== source.id) {
      lastSourceIdRef.current = source.id;
      setApiKey(cfg.api_key || "");
      setStationId(cfg.station_id || "");
    }
    setSavedCfg(cfg);
  }, [source, cfg]);

  const isConfigured = (savedCfg.api_key || "").length > 0 && (savedCfg.station_id || "").length > 0;
  const dirty = (apiKey !== (savedCfg.api_key || "")) || (stationId !== (savedCfg.station_id || ""));
  const canSync = isConfigured && !dirty && !busy;

  const handleSave = async () => {
    setSaveErr(null); setSaved(false);
    const next = { api_key: apiKey.trim(), station_id: stationId.trim() };
    try {
      await onSave(next);
      // Locally reflect the persisted state so the Sync button enables
      // immediately, before the parent's reload completes.
      setSavedCfg(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setSaveErr(e.userMessage || "Could not save configuration");
    }
  };

  let statusBadge = (
    <span className="pill pill-manual"><span className="pill-dot" />Not configured</span>
  );
  if (source.status === "error") {
    statusBadge = <span className="pill pill-error"><span className="pill-dot" />Error</span>;
  } else if (isConfigured && !dirty) {
    statusBadge = <span className="pill pill-connected"><span className="pill-dot" />Configured</span>;
  }

  return (
    <div className="space-y-4" data-testid="wunderground-pane">
      <Para>
        <span className="text-foreground font-medium">For homes with a Personal Weather Station.</span>{" "}
        Hyperlocal rain measurements at your own catchment are the gold
        standard for tank inflow modelling — much more accurate than gridded
        reanalysis at the kilometre scale.
      </Para>

      <div className="flex items-center gap-2">
        {statusBadge}
        {source.last_sync_at && (
          <span className="text-[11px] text-muted-foreground">last sync {relativeTime(source.last_sync_at)}</span>
        )}
      </div>

      <div className="card-base p-4 space-y-3">
        <Field
          label="Station ID"
          placeholder="e.g. IAALST71"
          value={stationId}
          onChange={(e) => setStationId(e.target.value)}
          testid="wu-station-id"
        />
        <Field
          label="API key"
          type="password"
          placeholder="Stored server-side · never exposed to the browser"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          testid="wu-api-key"
        />
        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="text-[11px] text-muted-foreground">
            {dirty ? "Unsaved changes" : isConfigured ? "Saved" : "Empty"}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={busy || !dirty || !apiKey.trim() || !stationId.trim()}
              className="px-3 py-1.5 rounded-md text-xs border border-border hover:bg-secondary disabled:opacity-50"
              data-testid="wu-save-config"
            >
              {busy ? "Saving…" : saved ? "Saved ✓" : "Save config"}
            </button>
            <button
              onClick={onSync}
              disabled={!canSync}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-foreground text-background text-xs font-medium disabled:opacity-50"
              data-testid="wu-sync"
              title={!isConfigured ? "Save credentials first" : dirty ? "Save before syncing" : ""}
            >
              <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
              {busy ? "Syncing…" : "Sync last 30 days"}
            </button>
          </div>
        </div>
      </div>

      {saveErr && (
        <div className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-2.5 flex items-center gap-2" data-testid="wu-save-error">
          <AlertTriangle className="w-3.5 h-3.5" />
          {saveErr}
        </div>
      )}
      {synced && (synced.errors || []).length === 0 && (
        <div className="text-xs text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 rounded-md p-2.5 flex items-center gap-2" data-testid="wu-sync-success">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Synced · created {synced.rows_created}, updated {synced.rows_updated}.
          {(synced.warnings || []).length > 0 && <span className="text-[#F59E0B] ml-2">{synced.warnings[0]}</span>}
        </div>
      )}
      {synced && (synced.errors || []).length > 0 && (
        <div className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-2.5 flex items-start gap-2" data-testid="wu-sync-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>{synced.errors.join(" · ")}</div>
        </div>
      )}

      <DiscreetNote>
        <ExternalLink className="w-3 h-3 inline mr-1" />
        Get a free PWS API key by signing in at{" "}
        <a href="https://www.wunderground.com/member/api-keys" target="_blank" rel="noreferrer" className="underline underline-offset-2">
          wunderground.com/member/api-keys
        </a>{" "}
        with your station ID. Free tier allows 1 500 calls / day · units = metric.
      </DiscreetNote>
    </div>
  );
}
